import { useRef, useState } from "react";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import Link from "@material-ui/core/Link";
import IconButton from "@material-ui/core/IconButton";
import DownloadIcon from "@material-ui/icons/GetApp";
import DeleteIcon from "@material-ui/icons/Delete";
import Paper from "@material-ui/core/Paper";
import { ControllerFieldState, ControllerRenderProps, UseFormSetError, UseFormClearErrors } from "react-hook-form";
import { useIconFieldStyles } from "./styles";
import InputLabel from "@material-ui/core/InputLabel";
import Tooltip from "@material-ui/core/Tooltip";
import { MdInfo } from "react-icons/md";

type Props = {
    label?: string;
    helperText?: string;
    field: ControllerRenderProps<{
        nome?: string;
        descricao?: string;
        peso?: number;
        ativo?: boolean;
        cor_tag?: string;
        cor_fonte?: string;
        sigla_senha?: string;
        icone?: string | File;
    }, "icone">
    fieldState: ControllerFieldState;
    setError: UseFormSetError<{
        nome?: string;
        descricao?: string;
        peso?: number;
        ativo?: boolean;
        cor_tag?: string;
        cor_fonte?: string;
        sigla_senha?: string;
        icone?: string | File;
    }>
    clearErrors: UseFormClearErrors<{
        icone?: string | File;
        nome?: string;
        descricao?: string;
        peso?: number;
        ativo?: boolean;
        cor_tag?: string;
        cor_fonte?: string;
        sigla_senha?: string;
    }>
};

export default function IconField({ label, helperText, field, fieldState, setError, clearErrors }: Props) {
    const inputRef = useRef<HTMLInputElement>(null);
    const [dragActive, setDragActive] = useState(false);
    const { classes } = useIconFieldStyles({ dragActive, isError: !!fieldState.error });

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            field.onChange(e.dataTransfer.files[0]);
        }
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(true);
    };

    const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            field.onChange(file);
        }
    };

    const handleDownload = () => {
        if (!field.value) return;
        let url = typeof field.value === "string"
            ? field.value
            : URL.createObjectURL(field.value);

        const link = document.createElement("a");
        link.href = url;
        link.download = "logo";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        if (typeof field.value !== "string") {
            URL.revokeObjectURL(url);
        }
    };

    const handleRemove = (e: React.MouseEvent) => {
        e.stopPropagation();
        field.onChange(undefined);
    };

    if (field.value) {
        const src = typeof field.value === "string" ? field.value : URL.createObjectURL(field.value);

        return (
            <Box>
                <Box display="flex" flexDirection="row" alignItems="center">
                    {label && (
                        <InputLabel
                            required
                            className={classes.label}
                        >
                            {label}
                        </InputLabel>
                    )}
                    <Tooltip title="O ícone será apresentado no Totem de atendimento preferencial. Deve possuir tamanho 240 x 240 e deve possuir extensão PNG.">
                        <IconButton size="small" style={{ paddingBottom: 14 }}>
                            <MdInfo />
                        </IconButton>
                    </Tooltip>
                </Box>
                <Paper
                    variant="outlined"
                    className={classes.previewContainer}
                >
                    <img
                        src={src}
                        alt="Ícone"
                        className={classes.preview}
                    />
                    <Box flex={1} />
                    <IconButton onClick={handleDownload}>
                        <DownloadIcon style={{ color: "#706E7A" }} />
                    </IconButton>
                    <IconButton onClick={handleRemove}>
                        <DeleteIcon style={{ color: "#706E7A" }} />
                    </IconButton>
                </Paper>
                {(helperText || fieldState.error) && (
                    <Typography
                        variant="caption"
                        color={fieldState.error ? "error" : "textSecondary"}
                        className={classes.helperText}
                    >
                        {fieldState.error?.message || helperText}
                    </Typography>
                )}
            </Box>
        );
    }

    return (
        <Box>
            <Box display="flex" flexDirection="row" alignItems="center">
                {label && (
                    <InputLabel
                        required
                        className={classes.label}
                        error={!!fieldState.error}
                    >
                        {label}
                    </InputLabel>
                )}
                <Tooltip title="O ícone será apresentado no Totem de atendimento preferencial. Deve possuir tamanho 240 x 240 e deve possuir extensão PNG.">
                    <IconButton size="small" style={{ paddingBottom: 14 }}>
                        <MdInfo />
                    </IconButton>
                </Tooltip>
            </Box>
            <div
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                className={classes.inputContainer}
                onClick={() => inputRef.current?.click()}
            >
                <input
                    ref={inputRef}
                    accept="image/png"
                    type="file"
                    style={{ display: "none" }}
                    id="icon-upload"
                    onChange={handleFileChange}
                />
                <CloudUploadIcon className={classes.uploadIcon} />
                <Typography variant="body2" color="textSecondary" component="div">
                    <Link
                        type="button"
                        component="button"
                        underline="hover"
                        className={classes.link}
                        onClick={e => {
                            e.stopPropagation();
                            inputRef.current?.click();
                        }}
                    >
                        Escolher arquivo
                    </Link>{" "}
                    ou arraste e solte aqui
                </Typography>
                <Typography
                    variant="caption"
                    color="textSecondary"
                    style={{ marginTop: 4 }}
                >
                    PNG (máx. 1MB)
                </Typography>
            </div>
            {(helperText || fieldState.error) && (
                <Typography
                    variant="caption"
                    color={fieldState.error ? "error" : "textSecondary"}
                    className={classes.helperText}
                >
                    {fieldState.error?.message || helperText}
                </Typography>
            )}
        </Box>
    );
}
